import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { type ContractService, errors, paxRoute, sessionRequired, staffAuth } from "../../common";
import {
  AddStaffMemberRequest,
  AssignStaffRolesRequest,
  CreateStaffRoleRequest,
  ListStaffRoleAssignmentsQuery,
  ListStaffRoleAssignmentsResponse,
  ListStaffRolesResponse,
  StaffMemberParams,
  StaffMemberResponse,
  StaffRoleAssignmentIdParams,
  StaffRoleAssignmentResponse,
  StaffRoleAssignmentsResponse,
  StaffRoleIdParams,
  StaffRoleResponse,
  UpdateStaffMemberRequest,
  UpdateStaffRoleRequest,
} from "./schemas";

export const service = {
  group: "staff",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Staff API",
  description: "Staff roles, the permissions they grant, and who holds them.",
} as const satisfies ContractService;

const managerAuth = staffAuth("staff.manage");

const noSuchRole = { NOT_FOUND: { message: "No staff role has that id." } } as const;

const noSuchAssignment = {
  NOT_FOUND: { message: "No staff role assignment has that id." },
} as const;

const noSuchMember = { NOT_FOUND: { message: "No staff member has that user id." } } as const;

export const listRoles = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/staff/v1alpha1/roles",
      operationId: "staff.roles.list",
      summary: "List staff roles.",
      description: "Returns every role, active and archived, with how many people hold each.",
      inputStructure: "detailed",
      successDescription: "Every staff role.",
    }),
  )
  .errors({
    ...sessionRequired,
    ...errors.forbidden,
  })
  .output(ListStaffRolesResponse);

export const createRole = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/staff/v1alpha1/roles",
      operationId: "staff.roles.create",
      summary: "Create a staff role.",
      description:
        "Creates a role with its permissions and suggested monthly pay. Only an admin can include an admin-only permission.",
      inputStructure: "detailed",
      successDescription: "The new role.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    CONFLICT: { message: "An active role already has that name, ignoring case." },
  })
  .input(z.object({ body: CreateStaffRoleRequest }))
  .output(StaffRoleResponse);

export const updateRole = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/staff/v1alpha1/roles/{roleId}",
      operationId: "staff.roles.update",
      summary: "Edit a staff role.",
      description:
        "Changes a role's name, description, permissions, or default pay. A permission change applies to everyone holding the role at once. A default pay change leaves everyone's pay alone.",
      inputStructure: "detailed",
      successDescription: "The edited role.",
    }),
  )
  .errors({
    BAD_REQUEST: { message: "Provide at least one role field and use valid field values." },
    ...sessionRequired,
    ...errors.forbidden,
    ...noSuchRole,
    CONFLICT: {
      message: "The role is archived, or another active role already has that name, ignoring case.",
    },
  })
  .input(z.object({ params: StaffRoleIdParams, body: UpdateStaffRoleRequest }))
  .output(StaffRoleResponse);

export const archiveRole = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/staff/v1alpha1/roles/{roleId}/archive",
      operationId: "staff.roles.archive",
      summary: "Archive a staff role.",
      description:
        "Stops the role from being assigned and unassigns it from everyone who holds it. Archiving it again changes nothing.",
      inputStructure: "detailed",
      successDescription: "The archived role.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    ...noSuchRole,
  })
  .input(z.object({ params: StaffRoleIdParams }))
  .output(StaffRoleResponse);

export const listAssignments = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/staff/v1alpha1/assignments",
      operationId: "staff.assignments.list",
      summary: "List staff role assignments.",
      description:
        "Returns who holds which role, newest first. Filter by person or role; `state=ended` returns roles that were unassigned.",
      inputStructure: "detailed",
      successDescription: "The matching assignments.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
  })
  .input(z.object({ query: ListStaffRoleAssignmentsQuery }))
  .output(ListStaffRoleAssignmentsResponse);

export const assignRoles = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/staff/v1alpha1/assignments",
      operationId: "staff.assignments.create",
      summary: "Assign staff roles.",
      description:
        "Gives a staff member one or more roles, and can set their pay in the same step. All the roles are given or none are. Someone unpaid starts at the roles' default pay added together. Only an admin can assign a role that holds an admin-only permission, or assign roles to an admin. Add someone to staff with `staff.members.create` first.",
      inputStructure: "detailed",
      successDescription: "The new assignments.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "Use valid field values, give each role once, keep pay within the pay cap, and give a pay date only to paid staff, today or later.",
    },
    ...sessionRequired,
    ...errors.forbidden,
    NOT_FOUND: { message: "No staff member or staff role has that id." },
    CONFLICT: {
      message:
        "A role is archived, the person already holds one of the roles, or the pay date is not after the last one paid.",
    },
  })
  .input(z.object({ body: AssignStaffRolesRequest }))
  .output(StaffRoleAssignmentsResponse);

export const unassignRole = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/staff/v1alpha1/assignments/{assignmentId}",
      operationId: "staff.assignments.delete",
      summary: "Unassign a staff role.",
      description:
        "Ends the assignment. The person loses the role's permissions on their next request, and the assignment stays in their role history. Unassigning an ended assignment changes nothing.",
      inputStructure: "detailed",
      successDescription: "The ended assignment.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    ...noSuchAssignment,
  })
  .input(z.object({ params: StaffRoleAssignmentIdParams }))
  .output(StaffRoleAssignmentResponse);

export const addMember = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/staff/v1alpha1/members",
      operationId: "staff.members.create",
      summary: "Add a staff member.",
      description:
        "Adds a person to staff with their title, notes, first roles, and pay, all at once. Only an admin can give a role that holds an admin-only permission, or add an admin to staff.",
      inputStructure: "detailed",
      successDescription: "The new staff member.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "Give at least one role, use valid field values, and give a pay date only to paid staff, today or later.",
    },
    ...sessionRequired,
    ...errors.forbidden,
    NOT_FOUND: { message: "No account or staff role has that id." },
    CONFLICT: {
      message: "The person is a guest or already a staff member, or a role is archived.",
    },
  })
  .input(z.object({ body: AddStaffMemberRequest }))
  .output(StaffMemberResponse);

export const getMember = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/staff/v1alpha1/members/{userId}",
      operationId: "staff.members.get",
      summary: "Read a staff member.",
      description:
        "Returns a person's staff title, notes, and pay, the roles they hold, and their role history. A person becomes a staff member when they are added to staff and stays one after their last role ends.",
      inputStructure: "detailed",
      successDescription: "The staff member.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    ...noSuchMember,
  })
  .input(z.object({ params: StaffMemberParams }))
  .output(StaffMemberResponse);

export const updateMember = oc
  .meta(
    paxRoute({ auth: managerAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/staff/v1alpha1/members/{userId}",
      operationId: "staff.members.update",
      summary: "Edit a staff member.",
      description:
        "Changes a staff member's title, private notes, or pay. New pay applies from the next pay date. Only an admin can edit an admin.",
      inputStructure: "detailed",
      successDescription: "The edited staff member.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "Provide at least one member field, use valid field values, and give a pay date only to paid staff, today or later.",
    },
    ...sessionRequired,
    ...errors.forbidden,
    ...noSuchMember,
    CONFLICT: { message: "The pay date is not after the last one paid." },
  })
  .input(z.object({ params: StaffMemberParams, body: UpdateStaffMemberRequest }))
  .output(StaffMemberResponse);
